/** Explicit interval plans. Durations configure the app and do not prescribe training intensity. */
export type FitnessIntervalKind = 'hiit' | 'sprint-intervals';
export type FitnessIntervalMovementId = 'walking' | 'running' | 'treadmill' | 'stationary-bike' | 'elliptical' | 'rowing-machine' | 'jumping-jack' | 'mountain-climber' | 'burpee' | 'jump-rope';
export type FitnessIntervalStageKind = 'warm-up' | 'effort' | 'recovery' | 'cool-down';
export interface FitnessIntervalInput {
  kind: FitnessIntervalKind;
  effortMovementId: FitnessIntervalMovementId;
  recoveryMovementId: 'rest' | 'walking';
  rounds: number;
  effortSeconds: number;
  recoverySeconds: number;
  warmupSeconds: number;
  cooldownSeconds: number;
  preparationMovementId?: FitnessIntervalMovementId;
}
export interface FitnessIntervalStage {
  readonly kind: FitnessIntervalStageKind;
  readonly movementId: FitnessIntervalMovementId | null;
  readonly round: number | null;
  readonly durationMs: number;
  readonly startMs: number;
  readonly endMs: number;
}
export interface FitnessIntervalPlan {
  readonly input: Readonly<FitnessIntervalInput>;
  readonly stages: readonly FitnessIntervalStage[];
  readonly totalMs: number;
}
export const FITNESS_INTERVAL_LIMITS = Object.freeze({ maxRounds: 30, maxStageSeconds: 3600, maxTotalSeconds: 10800 });
export const FITNESS_INTERVAL_MOVEMENTS: readonly FitnessIntervalMovementId[] = Object.freeze(['walking', 'running', 'treadmill', 'stationary-bike', 'elliptical', 'rowing-machine', 'jumping-jack', 'mountain-climber', 'burpee', 'jump-rope']);

export function fitnessIntervalKind(value: string): FitnessIntervalKind | null {
  if (['HIIT', 'free-cardio-hiit', 'cardio_hiit', 'fitness.exercises.cardio.hiit'].includes(value)) return 'hiit';
  return value === 'Sprint Intervals' ? 'sprint-intervals' : null;
}
const boundedInteger = (value: number, minimum: number, maximum: number) => Number.isSafeInteger(value) && value >= minimum && value <= maximum;
const validMovement = (value: unknown): value is FitnessIntervalMovementId => typeof value === 'string' && FITNESS_INTERVAL_MOVEMENTS.includes(value as FitnessIntervalMovementId);

/** No implicit modality, duration, round count, warm-up or cool-down is inserted. */
export function createFitnessIntervalPlan(input: FitnessIntervalInput): FitnessIntervalPlan {
  const { maxRounds, maxStageSeconds, maxTotalSeconds } = FITNESS_INTERVAL_LIMITS;
  if (!input || !['hiit', 'sprint-intervals'].includes(input.kind) || !validMovement(input.effortMovementId)
    || !['rest', 'walking'].includes(input.recoveryMovementId)
    || !boundedInteger(input.rounds, 1, maxRounds)
    || !boundedInteger(input.effortSeconds, 1, maxStageSeconds) || !boundedInteger(input.recoverySeconds, 1, maxStageSeconds)
    || !boundedInteger(input.warmupSeconds, 0, maxStageSeconds) || !boundedInteger(input.cooldownSeconds, 0, maxStageSeconds)
    || (input.warmupSeconds > 0 || input.cooldownSeconds > 0) && !validMovement(input.preparationMovementId)
    || input.preparationMovementId !== undefined && !validMovement(input.preparationMovementId)
    || input.kind === 'sprint-intervals' && !['running', 'treadmill'].includes(input.effortMovementId)) throw new Error('Invalid Fitness interval plan');
  const totalSeconds = input.warmupSeconds + input.rounds * (input.effortSeconds + input.recoverySeconds) + input.cooldownSeconds;
  if (!boundedInteger(totalSeconds, 1, maxTotalSeconds)) throw new Error('Fitness interval plan exceeds total duration');
  const stages: FitnessIntervalStage[] = [];
  let startMs = 0;
  const add = (kind: FitnessIntervalStageKind, movementId: FitnessIntervalMovementId | null, seconds: number, round: number | null) => {
    if (!seconds) return;
    const durationMs = seconds * 1000, endMs = startMs + durationMs;
    stages.push(Object.freeze({ kind, movementId, round, durationMs, startMs, endMs })); startMs = endMs;
  };
  add('warm-up', input.preparationMovementId ?? null, input.warmupSeconds, null);
  for (let round = 1; round <= input.rounds; round++) {
    add('effort', input.effortMovementId, input.effortSeconds, round);
    add('recovery', input.recoveryMovementId === 'rest' ? null : input.recoveryMovementId, input.recoverySeconds, round);
  }
  add('cool-down', input.preparationMovementId ?? null, input.cooldownSeconds, null);
  return Object.freeze({ input: Object.freeze({ ...input }), stages: Object.freeze(stages), totalMs: startMs });
}

export interface FitnessIntervalPosition {
  readonly stage: FitnessIntervalStage | null;
  readonly stageIndex: number;
  readonly elapsedMs: number;
  readonly stageElapsedMs: number;
  readonly stageRemainingMs: number;
  readonly completed: boolean;
}
/** Boundaries move to the next stage. Finishing a timer does not confirm or persist a set. */
export function resolveFitnessIntervalPosition(plan: FitnessIntervalPlan, activeMs: number): FitnessIntervalPosition {
  const elapsedMs = Math.min(plan.totalMs, Math.max(0, Number.isFinite(activeMs) ? activeMs : 0));
  if (elapsedMs >= plan.totalMs) return { stage: null, stageIndex: plan.stages.length, elapsedMs, stageElapsedMs: 0, stageRemainingMs: 0, completed: true };
  const stageIndex = plan.stages.findIndex(stage => elapsedMs < stage.endMs), stage = plan.stages[stageIndex];
  return { stage, stageIndex, elapsedMs, stageElapsedMs: elapsedMs - stage.startMs, stageRemainingMs: stage.endMs - elapsedMs, completed: false };
}

export interface FitnessIntervalClock {
  readonly scope: string;
  readonly elapsedMs: number;
  readonly startedAtMs: number | null;
}
export function createFitnessIntervalClock(scope: string): FitnessIntervalClock {
  if (!scope) throw new Error('Fitness interval clock needs an owner/session/set scope');
  return Object.freeze({ scope, elapsedMs: 0, startedAtMs: null });
}
const validNow = (nowMs: number) => Number.isFinite(nowMs) && nowMs >= 0;
/** Stale owner/session/set scopes cannot read or modify a newer clock. */
export function readFitnessIntervalClock(clock: FitnessIntervalClock, nowMs: number, scope: string): number | null {
  if (clock.scope !== scope || !validNow(nowMs)) return null;
  return clock.elapsedMs + (clock.startedAtMs === null ? 0 : Math.max(0, nowMs - clock.startedAtMs));
}
export function startFitnessIntervalClock(clock: FitnessIntervalClock, nowMs: number, scope: string): FitnessIntervalClock | null {
  if (clock.scope !== scope || !validNow(nowMs)) return null;
  return clock.startedAtMs === null ? Object.freeze({ ...clock, startedAtMs: nowMs }) : clock;
}
export function pauseFitnessIntervalClock(clock: FitnessIntervalClock, nowMs: number, scope: string): FitnessIntervalClock | null {
  const elapsedMs = readFitnessIntervalClock(clock, nowMs, scope);
  return elapsedMs === null ? null : Object.freeze({ ...clock, elapsedMs, startedAtMs: null });
}
